import { createRouter, createWebHistory } from 'vue-router'
import axios from 'axios'
import { setupPermissionGuards } from './guards'

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/Login.vue'),
    meta: { public: true }
  },
  {
    path: '/mfa',
    name: 'mfa',
    component: () => import('../views/MFA.vue'),
    meta: { public: true }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('../views/ResetPassword.vue'),
    meta: { public: true }
  },
  {
    path: '/',
    name: 'Dashboard',
    component: () => import('../views/Dashboard.vue')
  },
  {
    path: '/content-dashboard',
    name: 'ContentDashboard',
    component: () => import('../views/ContentDashboard.vue'),
    meta: { requiresPermission: 'content.view' }
  },
  {
    path: '/materials',
    name: 'Materials',
    component: () => import('../views/Materials.vue')
  },
  {
    path: '/materials/:id',
    name: 'MaterialDetail',
    component: () => import('../views/MaterialDetail.vue')
  },
  {
    path: '/recipes',
    name: 'Recipes',
    component: () => import('../views/Recipes.vue')
  },
  {
    path: '/products',
    name: 'Products',
    component: () => import('../views/Products.vue')
  },
  {
    path: '/recipes/:id',
    name: 'RecipeDetail',
    component: () => import('../views/RecipeDetail.vue')
  },
  {
    path: '/recipes/new',
    name: 'RecipeNew',
    component: () => import('../views/RecipeForm.vue')
  },
  {
    path: '/recipes/:id/edit',
    name: 'RecipeEdit',
    component: () => import('../views/RecipeForm.vue')
  },
  {
    path: '/batches',
    name: 'Batches',
    component: () => import('../views/Batches.vue')
  },
  {
    path: '/batches/new',
    name: 'BatchNew',
    component: () => import('../views/BatchForm.vue')
  },
  {
    path: '/batches/:id',
    name: 'BatchDetail',
    component: () => import('../views/BatchDetail.vue')
  },
  {
    path: '/batches/:id/edit',
    name: 'BatchEdit',
    component: () => import('../views/BatchForm.vue')
  },
  {
    path: '/scenarios',
    name: 'Scenarios',
    component: () => import('../views/Scenarios.vue')
  },
  {
    path: '/costs',
    name: 'Costs',
    component: () => import('../views/CostAnalysis.vue'),
    meta: { requiresPermission: 'materials.view_costs' }
  },
  {
    path: '/vendors',
    name: 'Vendors',
    component: () => import('../views/Vendors.vue'),
    meta: { requiresPermission: 'vendors.view' }
  },
  {
    path: '/vendor-pricing',
    name: 'VendorPricing',
    component: () => import('../views/VendorPricing.vue'),
    meta: { requiresPermission: 'materials.manage_pricing' }
  },
  {
    path: '/qr-redirects',
    name: 'QRRedirects',
    component: () => import('../views/QRRedirects.vue'),
    meta: { requiresPermission: 'qr_redirects.view' }
  },
  {
    path: '/newsletter-subscribers',
    name: 'NewsletterSubscribers',
    component: () => import('../views/NewsletterSubscribers.vue'),
    meta: { requiresPermission: 'newsletter.view' }
  },
  {
    path: '/advanced-optimization',
    name: 'AdvancedOptimization',
    component: () => import('../views/AdvancedOptimization.vue')
  },
  {
    path: '/inventory',
    name: 'MaterialInventory',
    component: () => import('../views/MaterialInventory.vue')
  },
  {
    path: '/product-inventory',
    name: 'ProductInventory',
    component: () => import('../views/ProductInventory.vue')
  },
  {
    path: '/purchase-orders',
    name: 'PurchaseOrders',
    component: () => import('../views/PurchaseOrders.vue'),
    meta: { requiresPermission: 'purchase_orders.view' }
  },
  {
    path: '/vendor-invoices',
    name: 'VendorInvoices',
    component: () => import('../views/VendorInvoices.vue'),
    meta: { requiresPermission: 'purchase_orders.view' }
  },
  {
    path: '/vendor-invoices/:id',
    name: 'VendorInvoiceDetail',
    component: () => import('../views/VendorInvoiceDetail.vue'),
    meta: { requiresPermission: 'purchase_orders.view' }
  },
  {
    path: '/payments',
    name: 'Payments',
    component: () => import('../views/Payments.vue'),
    meta: { requiresPermission: 'purchase_orders.view' }
  },
  // ─── Events revenue engine ─────────────────────────────────────────────
  // Mounted at /events in the ADMIN router. Note this does not collide with the
  // backend's /events (marketing attribution) — the API surface is /activations.
  {
    // The daily screen. Deliberately its own route rather than a tab on the
    // queue: an operator should be able to bookmark "what needs me".
    path: '/events/today',
    name: 'EventsAttention',
    component: () => import('../views/EventsAttention.vue'),
    meta: { requiresPermission: 'events.view' }
  },
  {
    path: '/events',
    name: 'EventsReviewQueue',
    component: () => import('../views/EventsReviewQueue.vue'),
    meta: { requiresPermission: 'events.view' }
  },
  {
    path: '/events/activations/:id',
    name: 'EventsActivationDetail',
    component: () => import('../views/EventsActivationDetail.vue'),
    meta: { requiresPermission: 'events.view' }
  },
  {
    path: '/events/discovered',
    name: 'EventsDiscovered',
    component: () => import('../views/EventsDiscovered.vue'),
    meta: { requiresPermission: 'events.view' }
  },
  {
    path: '/events/sources',
    name: 'EventsSources',
    component: () => import('../views/EventsSources.vue'),
    meta: { requiresPermission: 'events.view' }
  },

  // ─── Retail CRM ────────────────────────────────────────────────────────
  // A sibling domain to events: events answer "should we sell AT this?", these
  // answer "will they stock it?". Literal paths sit before the parameterised
  // one so /crm/accounts is never matched as an account id.
  {
    path: '/crm',
    redirect: '/crm/today'
  },
  {
    // The morning screen — the same payload the 07:30 digest sends, rendered.
    path: '/crm/today',
    name: 'CrmToday',
    component: () => import('../views/CrmToday.vue'),
    meta: { requiresPermission: 'crm.view' }
  },
  {
    path: '/crm/accounts',
    name: 'CrmAccounts',
    component: () => import('../views/CrmAccounts.vue'),
    meta: { requiresPermission: 'crm.view' }
  },
  {
    path: '/crm/pipeline',
    name: 'CrmPipeline',
    component: () => import('../views/CrmPipeline.vue'),
    meta: { requiresPermission: 'crm.view' }
  },
  {
    path: '/crm/calendar',
    name: 'CrmCalendar',
    component: () => import('../views/CrmCalendar.vue'),
    meta: { requiresPermission: 'crm.view' }
  },
  {
    path: '/crm/imports',
    name: 'CrmImports',
    component: () => import('../views/CrmImports.vue'),
    meta: { requiresPermission: 'crm.view' }
  },
  {
    path: '/crm/dashboard',
    name: 'CrmDashboard',
    component: () => import('../views/CrmDashboard.vue'),
    meta: { requiresPermission: 'crm.view' }
  },
  {
    // LAST of the /crm block: ':id' would otherwise swallow 'accounts',
    // 'pipeline' and the rest.
    path: '/crm/accounts/:id',
    name: 'CrmAccountDetail',
    component: () => import('../views/CrmAccountDetail.vue'),
    meta: { requiresPermission: 'crm.view' }
  },
  // ─── Fan art studio ────────────────────────────────────────────────────
  // Fans describe a picture, Vera draws it, and a human here decides whether it
  // joins the mosaic of Banjino's face on the homepage. Approving PUBLISHES —
  // there is no second gate downstream — so the actions on this screen sit
  // behind fanart.approve.
  {
    path: '/fan-art',
    name: 'FanArtQueue',
    component: () => import('../views/FanArtQueue.vue'),
    meta: { requiresPermission: 'fanart.view' }
  },
  // ─── Trade universe ────────────────────────────────────────────────────
  // The licensed world the CRM's accounts are chosen from, drawn around an
  // event's radius.
  {
    path: '/trade/map',
    name: 'TradeMap',
    component: () => import('../views/TradeMap.vue'),
    meta: { requiresPermission: 'trade.view' }
  },
  {
    path: '/trade/placements',
    name: 'TradePlacements',
    component: () => import('../views/TradePlacements.vue'),
    meta: { requiresPermission: 'trade.view' }
  },
  {
    path: '/compliance',
    name: 'Compliance',
    component: () => import('../views/Compliance.vue'),
    meta: { requiresPermission: 'compliance.view' }
  },
  {
    path: '/compliance/licenses',
    name: 'ComplianceLicenses',
    component: () => import('../views/ComplianceLicenses.vue'),
    meta: { requiresPermission: 'compliance.view' }
  },
  {
    path: '/compliance/obligations',
    name: 'ComplianceObligations',
    component: () => import('../views/ComplianceObligations.vue'),
    meta: { requiresPermission: 'compliance.view' }
  },
  {
    path: '/compliance/form-submissions',
    name: 'ComplianceFormSubmissions',
    component: () => import('../views/ComplianceFormSubmissions.vue'),
    meta: { requiresPermission: 'compliance.view' }
  },
  {
    path: '/compliance/form-submissions/:id',
    name: 'ComplianceFormFill',
    component: () => import('../views/ComplianceFormFill.vue'),
    meta: { requiresPermission: 'compliance.view' }
  },
  {
    path: '/compliance/requirements',
    name: 'ComplianceRequirements',
    component: () => import('../views/ComplianceRequirements.vue'),
    meta: { requiresPermission: 'compliance.view' }
  },
  {
    path: '/compliance/records',
    name: 'ComplianceRecords',
    component: () => import('../views/ComplianceRecords.vue'),
    meta: { requiresPermission: 'compliance.view' }
  },
  {
    path: '/compliance/audits',
    name: 'ComplianceAudits',
    component: () => import('../views/ComplianceAudits.vue'),
    meta: { requiresPermission: 'compliance.view' }
  },
  {
    path: '/api-keys',
    name: 'ApiKeys',
    component: () => import('../views/ApiKeys.vue'),
    meta: { requiresPermission: 'api_keys.manage' }
  },
  {
    // The MCP OAuth consent screen. The browser lands here from
    // api.banjinos.com/oauth/authorize, sent by ChatGPT / Claude / Cursor.
    //
    // It is NOT public: the whole point is that a logged-in Banjino admin
    // approves the grant, so the guard's redirect-to-login-and-come-back is the
    // mechanism, not an obstacle. Same permission as minting an API key,
    // because approving a connection IS minting one.
    path: '/oauth/consent',
    name: 'OAuthConsent',
    component: () => import('../views/OAuthConsent.vue'),
    meta: { requiresPermission: 'api_keys.manage' }
  },
  {
    path: '/content',
    name: 'Content',
    component: () => import('../views/Content.vue'),
    meta: { requiresPermission: 'content.view' }
  },
  {
    path: '/content-hub',
    name: 'ContentHub',
    component: () => import('../views/ContentHub.vue'),
    meta: { requiresPermission: 'content.manage' }
  },
  {
    path: '/content-studio',
    name: 'ContentStudio',
    component: () => import('../views/ContentStudio.vue'),
    meta: { requiresPermission: 'content.manage' }
  },
  {
    path: '/content-collections',
    name: 'ContentCollections',
    component: () => import('../views/ContentHub.vue'), // Reuse ContentHub for now
    meta: { requiresPermission: 'content_collections.view' }
  },
  {
    path: '/media-library',
    name: 'MediaLibrary',
    component: () => import('../views/ContentHub.vue'), // Reuse ContentHub for now
    meta: { requiresPermission: 'media.view' }
  },
  {
    path: '/content-analytics',
    name: 'ContentAnalytics',
    component: () => import('../views/ContentHub.vue'), // Reuse ContentHub for now
    meta: { requiresPermission: 'content.analytics' }
  },
  {
    path: '/test-content',
    name: 'TestContent',
    component: () => import('../views/TestContent.vue')
  },
  {
    path: '/clients',
    name: 'Clients',
    component: () => import('../views/Clients.vue'),
    meta: { requiresPermission: 'clients.view' }
  },
  {
    path: '/sales-orders',
    name: 'SalesOrders',
    component: () => import('../views/SalesOrders.vue'),
    meta: { requiresPermission: ['orders.view', 'crm.view'] }
  },
  {
    // Where the notification email for a scanned case order lands.
    path: '/order-requests',
    name: 'OrderRequests',
    component: () => import('../views/OrderRequests.vue'),
    meta: { requiresPermission: 'crm.view' }
  },
  {
    path: '/volume-forecasting',
    name: 'VolumeForecasting',
    component: () => import('../views/VolumeForecasting.vue')
  },
  {
    path: '/profit-projections',
    name: 'ProfitProjections',
    component: () => import('../views/ProfitProjections.vue'),
    meta: { requiresPermission: 'profit_projections.view' }
  },
  {
    path: '/ingredients',
    redirect: '/materials'
  },
  {
    path: '/font-tester',
    name: 'FontTester',
    component: () => import('../views/FontTester.vue'),
    meta: { development: true }
  },
  {
    path: '/font-validator',
    name: 'FontValidator',
    component: () => import('../views/FontValidator.vue'),
    meta: { development: true }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('../views/Profile.vue')
  },
  {
    path: '/forbidden',
    name: 'Forbidden',
    component: () => import('../views/Forbidden.vue'),
    meta: { public: true }
  },
  {
    path: '/test-permissions',
    name: 'TestPermissions',
    component: () => import('../views/TestPermissions.vue'),
    meta: { public: true }
  },
  {
    path: '/design-preview-2024',
    name: 'DesignPreview',
    component: () => import('../views/DesignPreview.vue'),
    meta: { public: true }
  },
  {
    path: '/brand-management',
    name: 'BrandManagement',
    component: () => import('../views/BrandManagement.vue'),
    meta: { requiresPermission: 'brand_management.view' }
  },
  {
    path: '/style-guide',
    name: 'StyleGuide',
    component: () => import('../views/StyleGuide.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/public/style-guide',
    name: 'PublicStyleGuide',
    component: () => import('../views/PublicStyleGuide.vue'),
    meta: { public: true }
  },
  {
    path: '/brand-guide',
    name: 'BrandGuidePublic', 
    redirect: '/public/style-guide',
    meta: { public: true }
  },
  {
    path: '/settings',
    name: 'CompanySettings',
    component: () => import('../views/CompanySettings.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/company-documents',
    name: 'CompanyDocuments',
    component: () => import('../views/CompanyDocuments.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/business',
    name: 'Business',
    component: () => import('../views/Business.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: () => import('../views/PrivacyPolicy.vue'),
    meta: { public: true }
  },
  {
    path: '/user-data-deletion',
    name: 'UserDataDeletion',
    component: () => import('../views/UserDataDeletion.vue'),
    meta: { public: true }
  },
  {
    path: '/terms-of-service',
    name: 'TermsOfService',
    component: () => import('../views/TermsOfService.vue'),
    meta: { public: true }
  },

  // User Management Routes
  {
    path: '/admin/users',
    name: 'Users',
    component: () => import('../views/admin/UserManagement.vue')
  },
  {
    path: '/admin/users/:id/permissions',
    name: 'UserPermissions',
    component: () => import('../views/admin/UserPermissions.vue'),
    meta: { requiresPermission: 'users.manage_permissions' }
  },

  // 404 catch-all - redirect to login or home based on auth status (MUST be last)
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    redirect: to => {
      const token = localStorage.getItem('token')
      if (token) {
        // User is logged in, redirect to home
        return '/'
      } else {
        // User is not logged in, redirect to login
        return '/login'
      }
    }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Defer guard setup until after app initialization
export function setupRouterGuards() {
  setupAxiosHeaders()
  setupPermissionGuards(router)
}

// Set auth header for axios - moved to setupRouterGuards to ensure proper initialization order
function setupAxiosHeaders() {
  router.beforeEach((to, from, next) => {
    const token = localStorage.getItem('token')
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
    }
    next()
  })
}

export default router